import { FormControlLabel, List, ListItem, Radio, RadioGroup, styled, Theme } from '@nexelem/deprecated-theme';
import { omit } from 'lodash';
import { ChangeEvent, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, checked, disabled }) => {
  const typedTheme = theme as Theme;
  const { getColor, hexToRGB } = theme.palette;
  const { radioListItem } = typedTheme.client.lists;

  const basicStyles = {
    '&&': {
      paddingBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      padding: theme.spacing(3, 2, 3, 4),
      width: '100%',
      paddingLeft: typedTheme.spacing(3),
    },
    '> span:first-child': {
      padding: theme.spacing(0, 2, 0, 0),
      '& .MuiSvgIcon-root': {
        fontSize: theme.typography.pxToRem(16),
        color: getColor(radioListItem.defaultState.svgColor),
      },
    },
    '> span:last-child': {
      ...typedTheme.typography.sourceSansPro16Regular,
      color: getColor(radioListItem.defaultState.color),
    },
    '&:hover': {
      backgroundColor: hexToRGB(getColor(radioListItem.hover.backgroundColor), 0.5),
    },
    '.Mui-checked': {
      span: {
        '& .MuiSvgIcon-root': {
          color: getColor(radioListItem.checked.svgColor),
        },
      },
    },
  };
  if (disabled) {
    return {
      ...omit(basicStyles, '&:hover'),
      '&& > span:last-child': {
        ...basicStyles['> span:last-child'],
        color: getColor(radioListItem.disabled.color),
      },
    };
  }
  if (checked) {
    return {
      ...basicStyles,
      backgroundColor: hexToRGB(getColor(radioListItem.checked.backgroundColor), 0.9),
    };
  }

  return {
    ...basicStyles,
  };
});

const StyledListItem = styled(ListItem)(({ theme }) => {
  return {
    margin: 0,
    padding: 0,
    marginTop: theme.spacing(1),
  };
});

export type RadioListProps<T = string> = {
  readonly defaultValue?: T;
  readonly className?: string;
  readonly value?: T | null;
  readonly options: T[];
  readonly getOptionLabel?: (option: T) => string;
  readonly renderLabel?: (option: T) => ReactNode;
  readonly getOptionId?: (option: T) => string | number;
  readonly isOptionDisabled?: (option: T) => boolean;
  readonly getOptionControl?: (option: T) => ReactElement | undefined | null;
  readonly onChange?: (value?: T) => void;
};

export function RadioList<T>({
  options,
  value,
  onChange,
  className,
  defaultValue,
  renderLabel,
  getOptionId,
  getOptionLabel = (option: T): string => option as string,
  isOptionDisabled = () => false,
  getOptionControl,
}: RadioListProps<T>) {
  const { t } = useTranslation();

  const handleChange = (event: ChangeEvent<HTMLInputElement>, value: string) => {
    const data = options.find((_) => getOptionLabel(_) === value);
    onChange && onChange(data as T);
  };

  const valueLabel = value ? getOptionLabel(value) : '';

  return (
    <RadioGroup className={className} defaultValue={defaultValue} value={valueLabel} onChange={handleChange}>
      {options.length > 0 && (
        <List>
          {options.map((option) => {
            const label = getOptionLabel(option);
            const key = getOptionId?.(option) || label;
            const isChecked = getOptionId && value ? getOptionId(option) === getOptionId(value) : label === valueLabel;
            const control = getOptionControl?.(option) || <Radio disableRipple size="small" />;
            return (
              <StyledListItem key={key}>
                <StyledFormControlLabel
                  checked={isChecked}
                  control={control}
                  disabled={isOptionDisabled(option)}
                  label={renderLabel ? renderLabel(option) : label}
                  value={label}
                />
              </StyledListItem>
            );
          })}
        </List>
      )}
      {options.length === 0 && (
        <div className="py-4 text-center">
          <span className="text-center text-s16 text-text-secondary">{t('components.lists.emptyState')}</span>
        </div>
      )}
    </RadioGroup>
  );
}
